<script setup lang="ts">
import type { Collection, ScheduledAction, ScheduledActionProps } from 'contentful-management'

interface ScheduleInfo {
  datetime: string
  timezone: string
  action: string
}

// Constants
const TIMEZONE_MAP = {
  'en-NZ': 'Pacific/Auckland',
  'fr-CA': 'Canada/Central',
  'en-CA': 'Canada/Central',
  'en-AU': 'Australia/Brisbane',
  default: 'Australia/Brisbane',
} satisfies Record<string, string>

const CONTENTTYPE_MAP = {
  content_page: 'Content Page',
  connectedContentPillar: 'Connected Content Pillar',
  blogArticle: 'Blog Article',
  productListingPage: 'Product Listing Page',
  pdpPreferences: 'Product Details Page',
  cartPreferences: 'My Bag',
  slug: 'Slug',
} satisfies Record<string, string>

const PREVIEW_FILTERS: PreviewFilters = {
  contentPage: usePreviewContentPageFilter,
  slug: usePreviewContentPageFilter,
  blogArticle: usePreviewBlogArticleFilter,
  connectedContentPillar: usePreviewConnectedPillarFilter,
  cartPreferences: usePreviewMyBagFilter,
  pdpPreferences: usePreviewProductDetailsPageFilter,
  productListingPage: usePreviewProductListingPageFilter,
}

const route = useRoute()
const show = ref(false)

// Composables
const {
  isPreviewEnabled,
  isPromotionPreview,
  previewId,
  previewEnv,
  previewByDate,
  previewPersonalizationTag,
  previewLocale,
  previewConfig,
  siteDate,
  customerGroup,
  getPreviewContentType,
} = useContentfulPreview()

const previewStore = useContentfulPreviewStore()

const isMobile = useIsMobile()
const { fetchScheduledActions } = useScheduledActions()

// Check if preview controls should be visible
const shouldShowControls = isPreviewEnabled && isMobile?.value === false

const contentType = ref('')
const panelIds = ref<string[]>([])

// Fetch scheduled actions (only when preview is enabled and not mobile)
const allSchedules = ref<Collection<ScheduledAction, ScheduledActionProps> | null>(null)

if (shouldShowControls) {
  try {
    allSchedules.value = await fetchScheduledActions({
      previewIds: previewId ? [previewId] : [],
      previewEnv,
    })
  } catch (error) {
    console.error('Error fetching scheduled actions:', error)
    allSchedules.value = null
  }

  // Set the initial content type
  contentType.value = getPreviewContentType(previewStore.entries, previewConfig)

  if (!previewStore.entries || !contentType.value) {
    panelIds.value = []
  }

  const filter = PREVIEW_FILTERS[contentType.value]
  if (!filter) {
    panelIds.value = []
  }

  const { getAllPanels } = filter()
  const panels = getAllPanels(previewStore.entries, previewConfig)
  panelIds.value = panels?.map((panel) => panel.id) ?? []
}

// Helper Functions
const extractScheduleInfo = (
  data: Collection<ScheduledAction, ScheduledActionProps> | null,
  targetIds: string[],
): ScheduleInfo[] => {
  if (!data?.items?.length) return []

  const currentDate = new Date().toISOString()
  return data.items
    .filter((item) => targetIds.includes(item.entity?.sys?.id) && item.scheduledFor?.datetime >= currentDate)
    .map((item) => ({
      datetime: item.scheduledFor?.datetime,
      timezone: item.scheduledFor?.timezone,
      action: item.action,
    }))
}

const formatDate = (dte: string): string => {
  if (!dte) return ''
  const timeZone = TIMEZONE_MAP[previewLocale ?? 'default'] ?? TIMEZONE_MAP.default
  const dateString = new Date(dte).toLocaleString('en-AU', { timeZone })
  return `${dateString} ${timeZone}`
}

const buildUrlWithQuery = (queryOverrides: Record<string, string>): string => {
  const query = { ...route.query, ...queryOverrides }
  return `${route.path}?${new URLSearchParams(query as Record<string, string>).toString()}`
}

const getUrlForSchedule = (schedule: string): string =>
  buildUrlWithQuery({ previewByDate: new Date(schedule).toISOString() })

// Formatted date for display
const formattedDate = computed(() => {
  const dateToFormat = isPromotionPreview ? siteDate : previewByDate
  return dateToFormat ? formatDate(dateToFormat) : 'Any'
})

// Schedules for content on the current page
const pageSchedules = extractScheduleInfo(allSchedules.value, previewId ? [previewId] : []).map((s) => s.datetime)

// Schedules for content NOT on the current page
const otherSchedules = () => {
  if (!allSchedules.value?.items) return []

  const currentDate = new Date().toISOString()
  const currentPageIds = new Set([...panelIds.value, previewId].filter(Boolean) as string[])

  const datetimes = allSchedules.value.items
    .filter((item) => {
      const isScheduledInFuture = item.scheduledFor?.datetime >= currentDate
      const isNotOnCurrentPage = !currentPageIds.has(item.entity?.sys?.id)
      return isScheduledInFuture && isNotOnCurrentPage
    })
    .map((s) => s.scheduledFor?.datetime)
    .filter((d): d is string => Boolean(d))

  return [...new Set(datetimes)]
}

// URL for toggling personalization
const urlWithToggledPersonalization = computed(() =>
  buildUrlWithQuery(
    isPromotionPreview
      ? {
          customerGroup: customerGroup === 'Brilliance+Members' ? 'Brilliance+Non-Members' : 'Brilliance+Members',
        }
      : {
          previewPersonalizationTag: previewPersonalizationTag === 'member' ? 'non-member' : 'member',
        },
  ),
)

// Display label for content type
const contentTypeLabel = computed(() => CONTENTTYPE_MAP[contentType.value ?? ''] ?? 'Unknown')
</script>

<template>
  <div v-if="shouldShowControls" :class="['preview-controls', { 'preview-controls--promotion': isPromotionPreview }]">
    <div class="preview-controls__header" @click.prevent="show = !show">
      <img class="preview-controls__toggle" alt="Contentful" src="/assets/sprite/svg/contentful.svg" />
      <span>Preview</span>
    </div>

    <div class="preview-controls__content" :class="{ 'preview-controls__content--show': show }">
      <ul class="preview-controls__list">
        <li><span class="preview-controls__label">Id:</span> {{ previewId }}</li>
        <li><span class="preview-controls__label">Environment:</span> {{ previewEnv }}</li>
        <li v-if="!isPromotionPreview">
          <span class="preview-controls__label">Locale:</span> {{ previewLocale ?? 'N/A' }}
        </li>
        <li><span class="preview-controls__label">Content Type:</span> {{ contentTypeLabel }}</li>
        <li v-if="!isPromotionPreview">
          <span class="preview-controls__label">Personalization:</span> {{ previewPersonalizationTag }}
        </li>
        <li v-if="isPromotionPreview">
          <span class="preview-controls__label">Group:</span> {{ customerGroup.replace(/\+/g, ' ') }}
        </li>
        <li><span class="preview-controls__label">Date:</span> {{ formattedDate }}</li>
      </ul>

      <a :href="urlWithToggledPersonalization" class="preview-controls__button">{{
        isPromotionPreview ? 'Toggle Group Membership' : 'Toggle Personalization'
      }}</a>

      <div v-if="!isPromotionPreview">
        <h5 class="preview-controls__subtitle">Schedule for content on the page ({{ pageSchedules.length }})</h5>
        <ul class="preview-controls__list preview-controls__list--schedule">
          <li v-for="schedule in pageSchedules" :key="schedule">
            <a :href="getUrlForSchedule(schedule)">{{ formatDate(schedule) }}</a>
          </li>
          <li v-if="pageSchedules.length === 0">No scheduled content</li>
        </ul>

        <h5 class="preview-controls__subtitle">Other Schedules ({{ otherSchedules().length }})</h5>
        <ul class="preview-controls__list preview-controls__list--schedule">
          <li v-for="schedule in otherSchedules()" :key="schedule">
            <a :href="getUrlForSchedule(schedule)">{{ formatDate(schedule) }}</a>
          </li>
          <li v-if="otherSchedules().length === 0">No other scheduled content</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="postcss">
body:hover .preview-controls {
  opacity: 0.7;
}

body .preview-controls:hover {
  opacity: 1;
}

.preview-controls {
  background-color: hsla(0, 0%, 100%, 0.75);
  border: 1px solid #efc000;
  box-shadow: 0 0 3px 0 #efc000;
  font-size: 16px;
  left: 8px;
  line-height: 16px;
  position: fixed;
  top: 64px;
  z-index: 6000;
  background: #efc000;
}

.preview-controls--promotion {
  border: 1px solid #852201;
  background: #852201;
}

.preview-controls__header {
  margin: 8px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 8px;
  border-radius: 8px 0 0 0;
}

.preview-controls--promotion .preview-controls__header {
  color: #fff;
}

.preview-controls__header--errors {
  background: #fee;
  border-left: 4px solid #f44;
}

.preview-controls__toggle {
  width: 24px;
  height: 24px;
}

.preview-controls__content {
  max-height: 0;
  overflow: hidden;
  width: 75px;
  background: #fff;
}

.preview-controls__content--show {
  max-height: 800px;
  width: 350px;
  overflow-y: auto;
  padding: 8px;
}

.preview-controls__list {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
}

.preview-controls__list li {
  font-size: 14px;
}

.preview-controls__list--error li {
  color: #d32f2f;
}

.preview-controls__list--schedule a {
  color: #1976d2;
  text-decoration: underline;
}

.preview-controls__list--schedule a:hover {
  color: #1565c0;
}

.preview-controls__label {
  margin-right: 8px;
  color: #9f138d;
}

.preview-controls__subtitle {
  margin: 16px 0 8px;
  font-size: 14px;
  font-weight: 600;
}

.preview-controls__note {
  font-size: 12px;
  font-weight: 400;
  color: #666;
}

.preview-controls__button {
  color: #fff;
  background-color: #000;
  cursor: pointer;
  display: inline-block;
  font-family: var(--font-secondary-semibold);
  font-size: var(--text-sm);
  letter-spacing: var(--letter-spacing-1);
  padding: 1rem 2.5rem;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  transition: all 0.3s;
  width: auto;
}

.preview-controls__button:hover {
  background: #1a2807;
}
</style>
